import { useHierarchyContext, useTlfContext, useTocContext } from "contexts/AppSettingsContext";
import { getTlfUrl } from "contexts/tlf/getTlfUrl";
import { useUnsavedChanges } from "contexts/UnsavedChangesContextProvider";
import { eTlfItemType } from "functions/api/enums";
import { TitlesFootnotesReadDto } from "functions/api/tlfItem/TitlesFootnotesReadDto";
import { ChevronLeft, ChevronRight } from "react-bootstrap-icons";

/**
 * Navigation controls for moving between ToC entries when viewing a report.
 *
 * @returns JSX controls to navigate to the previous/next ToC item or an empty fragment.
 */
export const TocNavigate = () => {
  const hierarchyContext = useHierarchyContext();
  const hierarchy = hierarchyContext?.hierarchy;
  const tocContext = useTocContext();
  const tlfContext = useTlfContext();

  const { navigate } = useUnsavedChanges("toc-navigate");

  if (!hierarchy || !tocContext || !tlfContext) return null;

  const tlf = tlfContext.tlfData;
  const toc = tocContext.state.tocItems
    .filter((item) => item.type !== eTlfItemType.section)
    .sort((a, b) => (a.tocOrder ?? 0) - (b.tocOrder ?? 0)) as TitlesFootnotesReadDto[];

  const thisTocItemIndex = toc.findIndex((item) => item.id === tlf?.id);
  const leftToc = thisTocItemIndex > 0 ? toc[thisTocItemIndex - 1] : null;
  const leftDest =
    leftToc && hierarchy.tocMajorVersion !== undefined ? getTlfUrl(hierarchy, leftToc) : null;
  const rightToc =
    thisTocItemIndex > -1 && thisTocItemIndex < toc.length - 1 ? toc[thisTocItemIndex + 1] : null;
  const rightDest =
    rightToc && hierarchy.tocMajorVersion !== undefined ? getTlfUrl(hierarchy, rightToc) : null;

  return (
    <div className="d-flex align-items-center">
      <ChevronLeft
        style={{
          cursor: leftDest ? "pointer" : "not-allowed",
          color: leftDest ? "black" : "grey",
        }}
        aria-label="Navigate to previous ToC entry"
        title={leftToc?.tocNumber}
        onClick={() => leftDest && navigate(leftDest)}
      />
      <ChevronRight
        style={{
          cursor: rightDest ? "pointer" : "not-allowed",
          color: rightDest ? "black" : "grey",
        }}
        aria-label="Navigate to next ToC entry"
        title={rightToc?.tocNumber}
        onClick={() => rightDest && navigate(rightDest)}
      />
    </div>
  );
};

TocNavigate.displayName = "TocNavigate";
